import { isDashboardDeclinedStatus, toDashboardDateKey } from "./dashboardSpend"

export type DashboardDeclinedDailyPoint = {
	date: string
	count: number
	amount: number
	amountAvailable: true
}

export type DashboardDeclinedBreakdownEntry = {
	key: string
	name: string
	daily: DashboardDeclinedDailyPoint[]
}

export type DashboardDeclinedBreakdown = {
	teams: DashboardDeclinedBreakdownEntry[]
	buyers: DashboardDeclinedBreakdownEntry[]
}

export type DashboardDeclinedChartPoint = {
	name: string
	fullDate: string
	total: number
	[key: string]: string | number
}

export type DashboardDeclinedChartSeries = {
	key: string
	name: string
	total: number
	totalAmount: number
	amountsAvailable: boolean
	color: string
}

export type DashboardDeclinedChart = {
	daily: DashboardDeclinedChartPoint[]
	series: DashboardDeclinedChartSeries[]
	total: number
	totalAmount: number
	amountsAvailable: boolean
}

export type DashboardDeclinedNames = {
	teams?: ReadonlyMap<string, string>
	buyers?: ReadonlyMap<string, string>
}

const UNASSIGNED_TEAM_KEY = "__unassigned_team__"
const UNASSIGNED_BUYER_KEY = "__unassigned_buyer__"
const DEFAULT_DECLINED_SERIES_LIMIT = 4

type BreakdownMode = keyof DashboardDeclinedBreakdown

const getTransactionParty = (
	transaction: ICompanyTrxTransaction,
	uidKey: "team_uid" | "buyer_uid"
) => [transaction.from, transaction.to].find((party) => party?.[uidKey])

const getBreakdownIdentity = (
	transaction: ICompanyTrxTransaction,
	mode: BreakdownMode,
	names: DashboardDeclinedNames
) => {
	if (mode === "teams") {
		const party = getTransactionParty(transaction, "team_uid")
		const key = transaction.team_uid || party?.team_uid || UNASSIGNED_TEAM_KEY
		return {
			key,
			name:
				transaction.team_name ||
				names.teams?.get(key) ||
				(key === UNASSIGNED_TEAM_KEY ? "Unassigned team" : "Unknown team")
		}
	}

	const party = getTransactionParty(transaction, "buyer_uid")
	const key = transaction.buyer_uid || party?.buyer_uid || UNASSIGNED_BUYER_KEY
	return {
		key,
		name:
			transaction.buyer_name ||
			names.buyers?.get(key) ||
			party?.name ||
			(key === UNASSIGNED_BUYER_KEY ? "Unassigned buyer" : "Unknown buyer")
	}
}

const buildBreakdown = (
	transactions: ICompanyTrxTransaction[],
	mode: BreakdownMode,
	names: DashboardDeclinedNames
) => {
	const groups = new Map<
		string,
		{ name: string; daily: Map<string, DashboardDeclinedDailyPoint> }
	>()

	transactions.forEach((transaction) => {
		if (!isDashboardDeclinedStatus(transaction.status)) return

		const date = toDashboardDateKey(transaction.created_at)
		if (!date) return

		const identity = getBreakdownIdentity(transaction, mode, names)
		const group = groups.get(identity.key) || {
			name: identity.name,
			daily: new Map<string, DashboardDeclinedDailyPoint>()
		}
		const point = group.daily.get(date) || {
			date,
			count: 0,
			amount: 0,
			amountAvailable: true as const
		}

		point.count += 1
		point.amount += Math.abs(Number(transaction.amount) || 0)
		group.daily.set(date, point)
		groups.set(identity.key, group)
	})

	return Array.from(groups.entries()).map(([key, group]) => ({
		key,
		name: group.name,
		daily: Array.from(group.daily.values()).sort((a, b) => a.date.localeCompare(b.date))
	}))
}

export const buildDashboardDeclinedBreakdown = (
	transactions: ICompanyTrxTransaction[],
	names: DashboardDeclinedNames = {}
): DashboardDeclinedBreakdown => ({
	teams: buildBreakdown(transactions, "teams", names),
	buyers: buildBreakdown(transactions, "buyers", names)
})

export const declinedAmountDataKey = (seriesKey: string) => `${seriesKey}__amount`

const buildChartPoint = (date: string): DashboardDeclinedChartPoint => {
	const pointDate = new Date(`${date}T12:00:00`)
	return {
		name: pointDate.toLocaleDateString("en-US", {
			month: "short",
			day: "numeric"
		}),
		fullDate: pointDate.toLocaleDateString("en-US", {
			year: "numeric",
			month: "short",
			day: "numeric"
		}),
		total: 0
	}
}

export const buildDashboardDeclinedChart = (
	items: DashboardDeclinedBreakdownEntry[],
	dateKeys: string[],
	colors: string[],
	seriesLimit = DEFAULT_DECLINED_SERIES_LIMIT
): DashboardDeclinedChart => {
	const dateKeySet = new Set(dateKeys)
	const sortedItems = items
		.map((item) => {
			const daily = item.daily.filter((point) => dateKeySet.has(point.date))
			return {
				...item,
				daily,
				total: daily.reduce((sum, day) => sum + Number(day.count || 0), 0),
				totalAmount: daily.reduce((sum, day) => sum + Number(day.amount || 0), 0),
				amountsAvailable: daily.every((day) => day.amountAvailable)
			}
		})
		.filter((item) => item.total > 0)
		.sort((a, b) => b.total - a.total)
		.slice(0, seriesLimit)
	const series: DashboardDeclinedChartSeries[] = sortedItems.map((item, index) => ({
		key: item.key,
		name: item.name,
		total: item.total,
		totalAmount: item.totalAmount,
		amountsAvailable: item.amountsAvailable,
		color: colors[index % colors.length] || "#64748B"
	}))
	const dateMap = new Map<string, DashboardDeclinedChartPoint>(
		dateKeys.map((date) => [date, buildChartPoint(date)])
	)

	sortedItems.forEach((item) => {
		item.daily.forEach((point) => {
			const existing = dateMap.get(point.date)
			if (!existing) return

			const count = Number(point.count || 0)
			existing[item.key] = count
			existing[declinedAmountDataKey(item.key)] = Number(point.amount || 0)
			existing.total = Number(existing.total || 0) + count
		})
	})

	const daily = dateKeys.map((date) => {
		const point = dateMap.get(date) || buildChartPoint(date)
		series.forEach((entry) => {
			if (point[entry.key] === undefined) point[entry.key] = 0
			if (point[declinedAmountDataKey(entry.key)] === undefined) {
				point[declinedAmountDataKey(entry.key)] = 0
			}
		})
		return point
	})

	return {
		daily,
		series,
		total: series.reduce((sum, entry) => sum + entry.total, 0),
		totalAmount: series.reduce((sum, entry) => sum + entry.totalAmount, 0),
		amountsAvailable: series.every((entry) => entry.amountsAvailable)
	}
}
